import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { genres } from '@/mocks/genres';
import SearchBox from './SearchBox';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '资讯', to: '/news' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const solid = !isHome || scrolled;
  const linkBase = 'text-sm font-medium transition-colors';
  const linkColor = solid ? 'text-foreground-700 hover:text-primary-600' : 'text-white/90 hover:text-white';

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-background-50/95 backdrop-blur border-b border-background-200' : 'bg-gradient-to-b from-black/55 via-black/25 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-6 lg:gap-10 min-w-0">
          <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-500 text-white flex items-center justify-center font-heading font-bold text-lg md:text-xl">
              韩
            </span>
            <span className={`font-heading font-bold text-xl md:text-2xl tracking-tight ${solid ? 'text-foreground-950' : 'text-white'}`}>
              韩剧
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {navLinks.slice(0, 1).map((l) => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `${linkBase} ${linkColor} ${isActive ? (solid ? 'text-primary-600' : 'text-white') : ''}`}>
                {l.label}
              </NavLink>
            ))}

            <div className="relative group">
              <button className={`${linkBase} ${linkColor} flex items-center gap-1 cursor-pointer whitespace-nowrap`}>
                分类
                <i className="ri-arrow-down-s-line text-base transition-transform group-hover:rotate-180"></i>
              </button>
              <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="w-[420px] bg-background-50 border border-background-200 rounded-lg p-3 grid grid-cols-2 gap-1">
                  {genres.map((g) => (
                    <Link
                      key={g.key}
                      to={`/category/${g.key}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md cursor-pointer hover:bg-primary-50 transition-colors group/item"
                    >
                      <span className="w-8 h-8 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center shrink-0 group-hover/item:bg-primary-500 group-hover/item:text-white transition-colors">
                        <i className="ri-bookmark-3-line text-sm"></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground-900">{g.name}</span>
                        <span className="block text-xs text-foreground-500 truncate">{g.desc}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {navLinks.slice(1).map((l) => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => `${linkBase} ${linkColor} ${isActive ? (solid ? 'text-primary-600' : 'text-white') : ''}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden lg:block w-72 xl:w-80">
            <SearchBox variant={solid ? 'light' : 'dark'} />
          </div>
          <Link
            to="/search"
            aria-label="搜索"
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
              solid ? 'text-foreground-700 hover:bg-background-100' : 'text-white hover:bg-white/15'
            }`}
          >
            <i className="ri-search-line text-lg"></i>
          </Link>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="菜单"
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
              solid ? 'text-foreground-900 hover:bg-background-100' : 'text-white hover:bg-white/15'
            }`}
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4">
            <SearchBox onSubmitted={() => setMobileOpen(false)} />
          </div>
          <nav className="px-4 pb-4 flex flex-col">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-base font-medium ${isActive ? 'text-primary-600 bg-primary-50' : 'text-foreground-800 hover:bg-background-100'}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="px-4 pb-6">
            <p className="px-3 text-xs font-semibold tracking-wider text-foreground-400 uppercase mb-2">题材分类</p>
            <div className="grid grid-cols-2 gap-2">
              {genres.map((g) => (
                <Link
                  key={g.key}
                  to={`/category/${g.key}`}
                  className="px-3 py-3 rounded-md bg-background-100 text-sm font-medium text-foreground-800 flex items-center justify-between"
                >
                  {g.name}
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}